/**
 * Maven Builder Landing Page - Custom Home Design and Build
 */

import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { ArrowRight, Home, Building2, Ruler, LogIn, Check } from 'lucide-react';
import PageContainer from '../components/PageContainer';

export default function MavenBuilderLanding() {
  const navigate = useNavigate();

  return (
    <PageContainer>
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-card/95 backdrop-blur-md border-border z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Home className="h-6 w-6 text-chart-5" />
            <span className="font-bold text-xl text-foreground">
              Maven Builders
            </span>
          </div>
          <Button 
            onClick={() => navigate('/login')}
            variant="outline"
            className="gap-2"
          >
            <LogIn className="h-4 w-4" />
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative mt-16 pt-12 pb-20 bg-gradient-to-b from-chart-5/25 to-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                We Transform Visions into Reality
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Residential and commercial projects completed on time, on budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/project')}
                  className="text-lg px-8 py-6 bg-chart-5/10 hover:bg-chart-5/10"
                >
                  Book a Consultation <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => {
                    document.querySelector('#expertise')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-lg px-8 py-6 border-chart-5/40 text-chart-5 hover:bg-chart-5/10"
                >
                  View Our Work
                </Button>
              </div>
            </div>
            <div className="relative">
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1762374974129-f9266d9c4efc?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBjb25zdHJ1Y3Rpb258ZW58MXx8fHwxNzcxOTc4NTE5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Modern residential construction"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Our Expertise Section */}
      <section id="expertise" className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-foreground">Our Expertise</h2>
          <p className="text-xl text-center text-muted-foreground mb-16 max-w-3xl mx-auto">
            Transforming Visions into Reality
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Residential Construction */}
            <div className="bg-card rounded-lg overflow-hidden shadow-lg border border-border">
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1600953960630-ef9fca38ec9b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjBsdXh1cnklMjBob3VzZSUyMGFyY2hpdGVjdHVyZXxlbnwxfHx8fDE3NzE5Nzg1MTl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Custom luxury home"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Home className="h-8 w-8 text-chart-5" />
                  <h3 className="text-2xl font-bold text-foreground">Residential Construction</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  From custom homes to major renovations, our team of experts delivers high-quality 
                  workmanship and attention to detail, ensuring your residential project is completed 
                  to the highest standards.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Custom home design and build</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Kitchen and bathroom remodeling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Home additions and extensions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Exterior renovations and landscaping</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Commercial Construction */}
            <div className="bg-card rounded-lg overflow-hidden shadow-lg border border-border">
              <ImageWithFallback 
                src="https://images.unsplash.com/photo-1768719951887-539ce3752a05?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDF8fHx8MTc3MTk3ODUxOXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Commercial building construction"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="h-8 w-8 text-chart-5" />
                  <h3 className="text-2xl font-bold text-foreground">Commercial Construction</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Whether you're building a new office, retail space, or industrial facility, our 
                  commercial construction services provide innovative solutions that meet your business needs.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Office building construction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Retail and restaurant build-outs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Industrial and warehouse facilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">Tenant improvements</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-foreground">
            Innovative Construction Solutions by Maven Builders
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-chart-5/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Ruler className="h-8 w-8 text-chart-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">Expert Craftsmanship</h3>
              <p className="text-muted-foreground">
                Years of experience and a talented team dedicated to delivering exceptional results 
                for every client.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-chart-5/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-chart-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">On Time, On Budget</h3>
              <p className="text-muted-foreground">
                We pride ourselves on meeting deadlines and staying within budget without compromising 
                on quality.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-chart-5/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="h-8 w-8 text-chart-5" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">Innovative Design</h3>
              <p className="text-muted-foreground">
                Creating innovative and functional spaces that exceed your expectations and stand the 
                test of time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-chart-5/25 to-chart-5/10 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            At Maven Builders, we are dedicated to delivering exceptional results. Let's turn your 
            vision into reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/project')}
              className="text-lg px-8 py-6 bg-white text-chart-5 hover:bg-muted/40"
            >
              Book a Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/project')}
              className="text-lg px-8 py-6 bg-transparent border-2 border-white text-white hover:bg-white/10"
            >
              View Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-card py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Home className="h-6 w-6 text-chart-5" />
              <span className="font-bold text-xl">Maven Builders</span>
            </div>
            <p className="text-muted-foreground mb-4">
              Transforming Visions into Reality
            </p>
            <p className="text-sm text-muted-foreground">
              © 2026 Maven Builders. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </PageContainer>
  );
}
